import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Plus, List, Clock, Eye, Trash2 } from 'lucide-react';
import AppHeader from '../../../components/AppHeader';
import BottomNav from '../../../components/BottomNav';
import ErrorPage from '../../../components/ErrorPage';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'https://drvivara.cloud/api' });
api.interceptors.request.use(c => { const t = localStorage.getItem('token') || sessionStorage.getItem('token'); if (t) c.headers.Authorization = `Bearer ${t}`; return c; });

const TABS = ['All', 'Reports', 'Scans', 'Lab Results', 'Prescriptions'];
const TYPE_MAP = { report: 'Reports', scan: 'Scans', lab_result: 'Lab Results', prescription: 'Prescriptions', other: 'Other' };
const TYPE_ICONS = { report: '📄', scan: '🔬', lab_result: '🧪', prescription: '💊', other: '📁' };

const MedicalRecords = () => {
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [records, setRecords] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [activeTab, setActiveTab] = useState('All');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [showUpload, setShowUpload] = useState(false);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadType, setUploadType] = useState('report');

  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const fetchRecords = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/medical-records/patient/${user._id}`);
      setRecords(res.data.data?.records || res.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load records');
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeline = async () => {
    try {
      const res = await api.get(`/medical-records/timeline/${user._id}`);
      setTimeline(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (user?._id) {
      fetchRecords();
      fetchTimeline();
    }
  }, [user?._id]);

  const handleUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { showError('Please select a file'); return; }
    if (!uploadTitle.trim()) { showError('Please enter a title'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('recordType', uploadType);
      fd.append('title', uploadTitle);
      await api.post('/medical-records/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      showSuccess('Record uploaded');
      setShowUpload(false);
      setUploadTitle('');
      if (fileRef.current) fileRef.current.value = '';
      fetchRecords();
      fetchTimeline();
    } catch (err) {
      showError(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this record?')) return;
    try {
      await api.delete(`/medical-records/${id}`);
      showSuccess('Record deleted');
      setRecords(prev => prev.filter(r => r._id !== id));
    } catch (err) {
      showError(err.response?.data?.message || 'Delete failed');
    }
  };

  const handleViewFile = async (record) => {
    try {
      const res = await api.get(`/medical-records/file/${record._id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
    } catch (err) {
      showError('Could not open file');
    }
  };

  const filteredRecords = activeTab === 'All'
    ? records
    : records.filter(r => TYPE_MAP[r.recordType] === activeTab);

  if (error) return <ErrorPage message={error} onRetry={fetchRecords} />;

  return (
    <div className="bg-dark min-h-screen flex flex-col">
      <AppHeader title="Medical Records" />
      <div className="flex-1 p-4 pb-24 overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <div className="flex gap-2">
            <button onClick={() => setViewMode('list')} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${viewMode === 'list' ? 'bg-gold text-dark' : 'bg-gray-800 text-gray-400'}`}><List size={14} className="inline mr-1" />List</button>
            <button onClick={() => { setViewMode('timeline'); fetchTimeline(); }} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${viewMode === 'timeline' ? 'bg-gold text-dark' : 'bg-gray-800 text-gray-400'}`}><Clock size={14} className="inline mr-1" />Timeline</button>
          </div>
          <button onClick={() => setShowUpload(true)} className="bg-gold text-dark px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-1">
            <Plus size={16} /> Upload
          </button>
        </div>

        <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
          {TABS.map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} className={`px-4 py-1.5 rounded-full text-xs whitespace-nowrap font-medium ${activeTab === tab ? 'bg-gold text-dark' : 'bg-gray-800 text-gray-400'}`}>{tab}</button>
          ))}
        </div>

        {loading ? (
          <div className="text-center text-gray-500 py-12">Loading...</div>
        ) : viewMode === 'timeline' ? (
          <div className="relative">
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-700" />
            {timeline.length > 0 ? timeline.map((item, i) => (
              <div key={item.id || i} className="ml-10 mb-6 relative">
                <div className="absolute -left-[22px] w-3 h-3 rounded-full bg-gold mt-1.5" />
                <div className="bg-gray-800 rounded-xl p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="text-xs text-gold font-medium">{TYPE_ICONS[item.type]} {item.type}</span>
                      {item.title && <h4 className="text-white font-medium mt-1">{item.title}</h4>}
                      {item.doctorName && <p className="text-xs text-gray-400">Dr. {item.doctorName}</p>}
                    </div>
                    {item.date && <span className="text-xs text-gray-500 shrink-0">{new Date(item.date).toLocaleDateString()}</span>}
                  </div>
                  {item.description && <p className="text-xs text-gray-400 mt-2">{item.description}</p>}
                </div>
              </div>
            )) : <p className="text-center text-gray-500 py-8">No timeline entries</p>}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredRecords.length > 0 ? filteredRecords.map(record => (
              <div key={record._id} className="bg-gray-800 rounded-xl p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <span className="text-2xl mt-1 shrink-0">{TYPE_ICONS[record.recordType]}</span>
                    <div className="min-w-0">
                      <h4 className="text-white font-medium truncate">{record.title}</h4>
                      <p className="text-xs text-gray-400">{record.doctor?.name ? `Dr. ${record.doctor.name}` : 'Self-uploaded'} · {new Date(record.recordedAt || record.createdAt).toLocaleDateString()}</p>
                      <div className="flex gap-2 mt-2 flex-wrap">
                        <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                          record.recordType === 'report' ? 'bg-blue-900/50 text-blue-300' :
                          record.recordType === 'scan' ? 'bg-green-900/50 text-green-300' :
                          record.recordType === 'lab_result' ? 'bg-purple-900/50 text-purple-300' :
                          'bg-orange-900/50 text-orange-300'
                        }`}>{record.recordType}</span>
                        <span className="text-xs bg-gray-700 text-gray-400 px-2 py-0.5 rounded">{record.fileType}</span>
                        {record.fileSize && <span className="text-xs text-gray-500">{(record.fileSize / 1024).toFixed(1)} KB</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 ml-2 shrink-0">
                    <button onClick={() => handleViewFile(record)} className="p-2 bg-gold/20 text-gold rounded-lg hover:bg-gold/30 transition" title="View"><Eye size={16} /></button>
                    <button onClick={() => handleDelete(record._id)} className="p-2 bg-yellow-400/20 text-yellow-400 rounded-lg hover:bg-yellow-400/30 transition" title="Delete"><Trash2 size={16} /></button>
                  </div>
                </div>
              </div>
            )) : <p className="text-center text-gray-500 py-8">No {activeTab.toLowerCase()} records</p>}
          </div>
        )}
      </div>

      {showUpload && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-gray-800 rounded-2xl p-6 w-full max-w-md border border-gold/30">
            <h3 className="text-lg font-bold text-gold mb-4">Upload Medical Record</h3>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-gray-400 mb-1 block font-medium">Record Type</label>
                <select value={uploadType} onChange={e => setUploadType(e.target.value)} className="w-full bg-gray-700 text-white rounded-lg p-3 text-sm border border-gray-600 focus:border-gold outline-none">
                  <option value="report">Report</option>
                  <option value="scan">Scan</option>
                  <option value="lab_result">Lab Result</option>
                  <option value="prescription">Prescription</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block font-medium">Title</label>
                <input value={uploadTitle} onChange={e => setUploadTitle(e.target.value)} className="w-full bg-gray-700 text-white rounded-lg p-3 text-sm border border-gray-600 focus:border-gold outline-none" placeholder="e.g., Blood Test Results" />
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block font-medium">File (PDF, JPG, PNG)</label>
                <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.gif" className="w-full text-white text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:bg-gold file:text-dark file:font-semibold hover:file:bg-gold/80 cursor-pointer" />
              </div>
              <div className="flex gap-3">
                <button onClick={() => setShowUpload(false)} className="flex-1 bg-gray-700 text-gray-300 py-3 rounded-xl text-sm font-medium">Cancel</button>
                <button onClick={handleUpload} disabled={uploading} className="flex-1 bg-gold text-dark py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50">
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default MedicalRecords;
